if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('arttheory-quick-add-product, product-info');
            if (!productElement) throw new Error('Quick add product markup was not returned.');

            const protectedJson = Array.from(
              productElement.querySelectorAll('[data-quick-add-variants], [data-quick-add-variant-media]')
            ).map((element) => ({
              selector: element.hasAttribute('data-quick-add-variants')
                ? '[data-quick-add-variants]'
                : '[data-quick-add-variant-media]',
              text: element.textContent,
            }));

            this.preprocessHTML(productElement);
            protectedJson.forEach(({ selector, text }) => {
              const element = productElement.querySelector(selector);
              if (element) element.textContent = text;
            });
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        productElement
          .querySelectorAll('sticky-add-to-cart, script[src*="sticky-add-to-cart"]')
          .forEach((element) => element.remove());

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

if (!customElements.get('arttheory-quick-add-product')) {
  customElements.define(
    'arttheory-quick-add-product',
    class ArtTheoryQuickAddProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialized || this.initializationScheduled) return;
        this.initializationScheduled = true;

        window.queueMicrotask(() => {
          this.initializationScheduled = false;
          if (this.isConnected) this.initialize();
        });
      }

      disconnectedCallback() {
        if (this.handleOutsidePointerDown) {
          document.removeEventListener('pointerdown', this.handleOutsidePointerDown);
        }
      }

      initialize() {
        if (this.initialized) return;

        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.variantPicker = this.querySelector('.qa-variants');
        this.optionSelects = Array.from(this.querySelectorAll('.qa-select-input'));
        this.optionCatalogs = this.optionSelects.map((select) =>
          Array.from(select.options).map((option) => ({
            value: option.value,
            label: option.value,
            optionValueId: option.dataset.optionValueId || '',
            productUrl: option.dataset.productUrl || '',
          }))
        );
        this.availabilityNote = this.querySelector('[data-quick-add-availability]');
        this.priceContainer = this.querySelector('.qa-price-row');
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.errorWrapper?.querySelector('.product-form__error-message');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.variants = this.parseVariants();
        this.variantMedia = this.parseVariantMedia();
        this.initializeGallery();

        if (!this.form || !this.variantInput || !this.submitButton || !this.variants.length) {
          this.showError(window.variantStrings?.unavailable || 'This product is unavailable.');
          this.submitButton?.setAttribute('disabled', 'disabled');
          return;
        }

        this.initialized = true;
        this.initializeCustomSelects();

        this.variantPicker?.addEventListener('change', () => {
          this.clearError();
          this.updateOptionAvailability();
          this.syncSelectedVariant();
        });
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.updateOptionAvailability();
        this.syncSelectedVariant();
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-quick-add-variants]')?.textContent || '[]');
        } catch (error) {
          console.error('Unable to parse quick add variants', error);
          return [];
        }
      }

      parseVariantMedia() {
        try {
          return JSON.parse(this.querySelector('[data-quick-add-variant-media]')?.textContent || '{}');
        } catch (error) {
          console.error('Unable to parse quick add variant media', error);
          return {};
        }
      }

      initializeGallery() {
        this.galleryShell = this.querySelector('.qa-gallery-shell');
        this.galleryViewport = this.querySelector('[data-gallery-viewport]');
        this.gallerySlides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
        this.galleryCurrent = this.querySelector('[data-gallery-current]');
        this.galleryPrevious = this.querySelector('[data-gallery-previous]');
        this.galleryNext = this.querySelector('[data-gallery-next]');
        this.galleryIndex = 0;

        if (!this.galleryViewport || !this.gallerySlides.length) return;

        this.galleryPrevious?.addEventListener('click', () => this.moveGallery(-1));
        this.galleryNext?.addEventListener('click', () => this.moveGallery(1));

        let galleryFrame;
        this.galleryViewport.addEventListener(
          'scroll',
          () => {
            if (galleryFrame) return;
            galleryFrame = window.requestAnimationFrame(() => {
              galleryFrame = null;
              this.updateGalleryState(this.getClosestGalleryIndex());
            });
          },
          { passive: true }
        );

        this.updateGalleryState(0);
      }

      getClosestGalleryIndex() {
        if (!this.galleryViewport || !this.gallerySlides.length) return 0;

        const scrollCenter = this.galleryViewport.scrollLeft + this.galleryViewport.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.gallerySlides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(scrollCenter - slideCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      moveGallery(direction) {
        if (!this.gallerySlides.length) return;
        const nextIndex =
          (this.galleryIndex + direction + this.gallerySlides.length) % this.gallerySlides.length;
        this.scrollToGalleryIndex(nextIndex);
      }

      showMedia(mediaId, behavior = 'smooth') {
        if (!mediaId) return;
        const mediaIndex = this.gallerySlides.findIndex(
          (slide) => String(slide.dataset.mediaId) === String(mediaId)
        );
        if (mediaIndex !== -1) this.scrollToGalleryIndex(mediaIndex, behavior);
      }

      scrollToGalleryIndex(index, behavior = 'smooth') {
        const slide = this.gallerySlides[index];
        if (!slide || !this.galleryViewport) return;

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const resolvedBehavior = reducedMotion ? 'auto' : behavior;

        if (typeof this.galleryViewport.scrollTo === 'function') {
          this.galleryViewport.scrollTo({ left: slide.offsetLeft, behavior: resolvedBehavior });
        } else {
          this.galleryViewport.scrollLeft = slide.offsetLeft;
        }
        this.updateGalleryState(index);
      }

      updateGalleryState(index) {
        if (!this.gallerySlides[index]) return;
        this.galleryIndex = index;
        if (this.galleryCurrent) this.galleryCurrent.textContent = String(index + 1);
      }

      initializeCustomSelects() {
        this.customSelectControls = new Map();

        this.optionSelects.forEach((select) => {
          const wrapper = select.closest('.qa-select-wrapper');
          const label = select.closest('.qa-dropdown-group')?.querySelector('.qa-dropdown-label');
          if (!wrapper || !select.id) return;

          const trigger = document.createElement('button');
          const triggerId = `${select.id}-trigger`;
          const menu = document.createElement('div');
          const menuId = `${select.id}-menu`;
          const value = document.createElement('span');
          const originalIcon = Array.from(wrapper.children).find((child) =>
            child.classList?.contains('qa-select-icon')
          );

          trigger.type = 'button';
          trigger.id = triggerId;
          trigger.className = 'qa-custom-select-trigger';
          trigger.setAttribute('role', 'combobox');
          trigger.setAttribute('aria-haspopup', 'listbox');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('aria-controls', menuId);
          if (select.getAttribute('aria-describedby')) {
            trigger.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
          }

          value.className = 'qa-custom-select-value';
          trigger.append(value);
          if (originalIcon) {
            const customIcon = originalIcon.cloneNode(true);
            customIcon.classList.add('qa-custom-select-icon');
            trigger.append(customIcon);
          }

          menu.id = menuId;
          menu.className = 'qa-custom-select-menu';
          menu.setAttribute('role', 'listbox');
          menu.setAttribute('aria-labelledby', label?.id || triggerId);
          menu.hidden = true;

          wrapper.append(trigger, menu);
          wrapper.classList.add('is-enhanced');
          select.tabIndex = -1;
          select.setAttribute('aria-hidden', 'true');
          if (label) {
            label.id = label.id || `${select.id}-label`;
            label.htmlFor = triggerId;
            menu.setAttribute('aria-labelledby', label.id);
          }

          const control = { select, wrapper, trigger, menu, value, options: [] };
          this.customSelectControls.set(select, control);

          trigger.addEventListener('click', () => {
            if (menu.hidden) this.openCustomSelect(control);
            else this.closeCustomSelect(control, true);
          });
          trigger.addEventListener('keydown', (event) => this.onCustomSelectTriggerKeydown(event, control));
          this.syncCustomSelect(select);
        });

        this.handleOutsidePointerDown = (event) => {
          const selectWrapper = event.target.closest?.('.qa-select-wrapper');
          if (!selectWrapper || !this.contains(selectWrapper)) this.closeAllCustomSelects();
        };
        document.addEventListener('pointerdown', this.handleOutsidePointerDown);
      }

      syncCustomSelect(select) {
        const control = this.customSelectControls?.get(select);
        if (!control) return;

        const selectedOption = select.selectedOptions[0];
        control.value.textContent = selectedOption?.textContent.trim() || '';
        control.trigger.disabled = select.disabled;
        control.menu.replaceChildren();
        control.options = Array.from(select.options).map((nativeOption, index) => {
          const option = document.createElement('div');
          option.className = 'qa-custom-select-option';
          option.id = `${control.menu.id}-option-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('tabindex', '-1');
          option.setAttribute('aria-selected', String(nativeOption.value === select.value));
          option.dataset.value = nativeOption.value;
          option.textContent = nativeOption.textContent.trim();
          option.addEventListener('click', () => this.chooseCustomSelectOption(control, option));
          option.addEventListener('keydown', (event) => this.onCustomSelectOptionKeydown(event, control, option));
          control.menu.append(option);
          return option;
        });
      }

      openCustomSelect(control, focusDirection = 0) {
        this.closeAllCustomSelects(control);
        control.menu.hidden = false;
        control.wrapper.classList.add('is-open');
        control.trigger.setAttribute('aria-expanded', 'true');

        const triggerRect = control.trigger.getBoundingClientRect();
        const modalBoundary = this.closest('.quick-add-modal__content')?.getBoundingClientRect();
        const boundaryTop = modalBoundary?.top ?? 0;
        const boundaryBottom = modalBoundary?.bottom ?? window.innerHeight;
        const menuHeight = Math.min(control.menu.scrollHeight, 232);
        const spaceBelow = boundaryBottom - triggerRect.bottom;
        const spaceAbove = triggerRect.top - boundaryTop;
        control.menu.classList.toggle('opens-up', spaceBelow < menuHeight + 14 && spaceAbove > spaceBelow);

        const selectedIndex = Math.max(
          0,
          control.options.findIndex((option) => option.getAttribute('aria-selected') === 'true')
        );
        const focusIndex = focusDirection < 0 ? control.options.length - 1 : selectedIndex;
        window.requestAnimationFrame(() => control.options[focusIndex]?.focus());
      }

      closeCustomSelect(control, returnFocus = false) {
        if (!control || control.menu.hidden) return;
        control.menu.hidden = true;
        control.menu.classList.remove('opens-up');
        control.wrapper.classList.remove('is-open');
        control.trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) control.trigger.focus();
      }

      closeAllCustomSelects(exceptControl) {
        this.customSelectControls?.forEach((control) => {
          if (control !== exceptControl) this.closeCustomSelect(control);
        });
      }

      chooseCustomSelectOption(control, option) {
        if (!option?.dataset.value) return;
        control.select.value = option.dataset.value;
        this.closeCustomSelect(control, true);
        control.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onCustomSelectTriggerKeydown(event, control) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.openCustomSelect(control, event.key === 'ArrowUp' ? -1 : 1);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCustomSelect(control, true);
        }
      }

      onCustomSelectOptionKeydown(event, control, option) {
        const currentIndex = control.options.indexOf(option);
        let nextIndex;

        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % control.options.length;
        else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + control.options.length) % control.options.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = control.options.length - 1;
        else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.chooseCustomSelectOption(control, option);
          return;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCustomSelect(control, true);
          return;
        } else if (event.key === 'Tab') {
          this.closeCustomSelect(control);
          return;
        } else if (event.key.length === 1) {
          const search = event.key.toLocaleLowerCase();
          nextIndex = control.options.findIndex((candidate) =>
            candidate.textContent.trim().toLocaleLowerCase().startsWith(search)
          );
        } else {
          return;
        }

        if (nextIndex >= 0) {
          event.preventDefault();
          control.options[nextIndex]?.focus();
        }
      }

      getSelectedOptions() {
        return this.optionSelects.map((select) => select.value);
      }

      updateOptionAvailability() {
        if (!this.optionSelects.length) return;

        const selectedOptions = [];
        let availabilitySummary = null;

        this.optionSelects.forEach((select, optionIndex) => {
          const previousValue = select.value;
          const matchingVariants = this.variants.filter(
            (variant) =>
              variant.available &&
              Array.isArray(variant.options) &&
              selectedOptions.every(
                (selectedValue, selectedIndex) =>
                  String(variant.options[selectedIndex]) === selectedValue
              )
          );
          const availableValues = new Set(
            matchingVariants.map((variant) => String(variant.options[optionIndex]))
          );
          const availableOptions = this.optionCatalogs[optionIndex].filter((option) =>
            availableValues.has(option.value)
          );
          const nextValue = availableValues.has(previousValue)
            ? previousValue
            : availableOptions[0]?.value || '';
          const optionsFragment = document.createDocumentFragment();

          availableOptions.forEach((optionData) => {
            const option = document.createElement('option');
            option.value = optionData.value;
            option.textContent = optionData.label;
            option.selected = optionData.value === nextValue;
            if (optionData.optionValueId) option.dataset.optionValueId = optionData.optionValueId;
            if (optionData.productUrl) option.dataset.productUrl = optionData.productUrl;
            optionsFragment.append(option);
          });

          if (!availableOptions.length) {
            const unavailableOption = document.createElement('option');
            unavailableOption.textContent = this.submitButton.dataset.labelUnavailable;
            unavailableOption.value = '';
            unavailableOption.disabled = true;
            unavailableOption.selected = true;
            optionsFragment.append(unavailableOption);
          }

          select.replaceChildren(optionsFragment);
          select.value = nextValue;
          select.disabled = !availableOptions.length;
          this.syncCustomSelect(select);
          selectedOptions.push(nextValue);

          if (
            !availabilitySummary &&
            optionIndex > 0 &&
            availableOptions.length > 0 &&
            availableOptions.length < this.optionCatalogs[optionIndex].length
          ) {
            availabilitySummary = {
              context: selectedOptions.slice(0, optionIndex).join(' / '),
              label: this.getOptionLabel(select),
              values: availableOptions.map((option) => option.label),
            };
          }
        });

        this.updateAvailabilityNote(availabilitySummary);
      }

      getOptionLabel(select) {
        return select.closest('.qa-dropdown-group')?.querySelector('.qa-dropdown-label')?.textContent.trim() ||
          'options';
      }

      updateAvailabilityNote(summary) {
        const noteText = this.availabilityNote?.querySelector('span');
        if (!noteText) return;

        noteText.textContent = summary
          ? `${summary.context} — available ${summary.label.toLowerCase()}: ${summary.values.join(', ')}.`
          : 'Only available combinations are shown.';
      }

      resolveSelectedVariant() {
        const selectedOptions = this.getSelectedOptions();

        if (!selectedOptions.length) {
          const currentVariantId = String(this.variantInput?.value || '');
          return (
            this.variants.find((variant) => String(variant.id) === currentVariantId) ||
            this.variants.find((variant) => variant.available) ||
            this.variants[0]
          );
        }

        return this.variants.find(
          (variant) =>
            Array.isArray(variant.options) &&
            variant.options.length === selectedOptions.length &&
            variant.options.every((option, index) => String(option) === selectedOptions[index])
        );
      }

      syncSelectedVariant() {
        const variant = this.resolveSelectedVariant();
        this.selectedVariant = variant || null;
        this.variantInput.value = variant ? String(variant.id) : '';
        this.variantInput.disabled = !variant;

        this.updatePrice(variant);
        this.updateMedia(variant);
        this.updateSubmitButton(variant);

        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        return variant;
      }

      updatePrice(variant) {
        if (!this.priceContainer || !variant) return;

        const priceTemplate = this.querySelector(`template[data-variant-price="${variant.id}"]`);
        if (priceTemplate) {
          this.priceContainer.replaceChildren(priceTemplate.content.cloneNode(true));
        }
      }

      updateMedia(variant) {
        if (!variant) return;

        this.updateGalleryVariantMode(variant);

        const mediaId = this.variantMedia[String(variant.id)] || variant.featured_media?.id;
        if (!mediaId) return;

        this.showMedia(mediaId, this.hasSyncedVariantMedia ? 'smooth' : 'auto');
        this.hasSyncedVariantMedia = true;
      }

      updateGalleryVariantMode(variant) {
        if (!this.galleryShell) return;

        const isGalleryFrame = (variant.options || []).some((option) =>
          String(option).toLowerCase().includes('gallery frame')
        );
        this.galleryShell.classList.toggle('qa-gallery-shell--portrait', isGalleryFrame);
      }

      updateSubmitButton(variant) {
        const available = Boolean(variant?.available);
        const label = !variant
          ? this.submitButton.dataset.labelUnavailable
          : available
            ? this.submitButton.dataset.labelAddToCart
            : this.submitButton.dataset.labelSoldOut;

        this.submitButton.toggleAttribute('disabled', !available);
        this.submitButton.setAttribute('aria-disabled', String(!available));
        if (this.submitButtonText && label) this.submitButtonText.textContent = label;
      }

      async onSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const variant = this.syncSelectedVariant();
        if (!variant || !variant.available) {
          this.showError(
            !variant
              ? this.submitButton.dataset.labelUnavailable
              : this.submitButton.dataset.labelSoldOut
          );
          return;
        }

        this.clearError();
        this.setLoading(true);

        const formData = new FormData(this.form);
        formData.set('id', String(variant.id));
        if (!formData.has('quantity')) formData.set('quantity', '1');

        if (this.cart) {
          formData.set(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.set('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          const response = await fetch(window.routes.cart_add_url, config);
          const responseData = await response.json();

          if (!response.ok || responseData.status) {
            throw new Error(responseData.description || responseData.message || window.cartStrings.error);
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              data: {
                source: 'arttheory-quick-add',
                productVariantId: String(variant.id),
                cartData: responseData,
              },
            });
          }

          this.trackAddToCart(variant);
          this.finishCartUpdate(responseData);
        } catch (error) {
          console.error('Quick add failed', error);
          this.showError(error.message || window.cartStrings.error);
        } finally {
          this.setLoading(false);
        }
      }

      finishCartUpdate(responseData) {
        if (!this.cart) {
          window.location.assign(window.routes.cart_url);
          return;
        }

        const renderCart = () => {
          if (document.querySelector('cart-drawer')) {
            this.cart.renderContents(responseData, false);
            document.dispatchEvent(new CustomEvent('product:added', { detail: { item: responseData } }));
          } else {
            this.cart.renderContents(responseData);
          }
        };

        const quickAddModal = this.closest('quick-add-modal');
        if (!quickAddModal) {
          renderCart();
          return;
        }

        document.body.addEventListener('modalClosed', () => window.setTimeout(renderCart), { once: true });
        quickAddModal.hide(true);
      }

      setLoading(loading) {
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-disabled', String(loading));
        this.submitButton.toggleAttribute('disabled', loading || !this.selectedVariant?.available);
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      }

      clearError() {
        this.errorWrapper?.setAttribute('hidden', '');
        if (this.errorMessage) this.errorMessage.textContent = '';
      }

      trackAddToCart(variant) {
        try {
          window.ArtTheoryAnalytics?.track?.('add_to_cart', {
            source: 'arttheory-quick-add',
            product_id: this.dataset.productId,
            variant_id: String(variant.id),
            variant_options: this.getSelectedOptions(),
          });
        } catch (error) {
          console.warn('Quick add analytics tracking failed', error);
        }
      }
    }
  );
}
